import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../Components/Sidebar';
import { auth, db, storage } from '../firebaseConfig';
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  getDoc,
  query,
  orderBy,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaTrashAlt, FaCloudUploadAlt, FaSpinner, FaTimes } from 'react-icons/fa';
import '../Style/EditFailureReport.css';

/* ───────── helpers ───────── */
const fmtDate = d => {
  if (!d) return '';
  if (typeof d === 'string') return d.slice(0, 10);
  if (d.toDate) return d.toDate().toISOString().slice(0, 10);
  if (d instanceof Date) return d.toISOString().slice(0, 10);
  return '';
};

const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/* ═════════════════ component ═════════════════ */
export default function EditFailureReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [initialData, setInitialData] = useState(null);
  const [info, setInfo] = useState({
    reportId: '',
    jobId: '',
    status: 'Pending',
    date: '',
    customer: '',
    equipment: '',
    serial: '',
  });
  const [docs, setDocs] = useState([{ name: '', desc: '', files: [], previews: [] }]);
  const [errors, setErrors] = useState({ jobId: false, customer: false, docs: [] });

  /* ───────── fetch data on mount ───────── */
  useEffect(() => {
    const loadData = async user => {
      if (!user) {
        toast.error('Please login to edit a report.', { className: 'toast-error' });
        navigate('/');
        setLoading(false);
        return;
      }

      try {
        // Fetch failure report
        const reportDoc = await getDoc(doc(db, 'FailureReports', id));
        if (!reportDoc.exists()) {
          toast.error('Failure report not found.', { className: 'toast-error' });
          navigate('/Jobs');
          return;
        }
        const data = reportDoc.data();
        const reportData = {
          reportId: data.reportId || '',
          jobId: data.jobId || '',
          status: data.status || 'Pending',
          date: fmtDate(data.createdAt) || new Date().toISOString().slice(0, 10),
          customer: data.customer || '',
          equipment: data.equipment || '',
          serial: data.serial || '',
          docs: data.docs && data.docs.length > 0
            ? data.docs.map(d => ({
                name: d.name || '',
                desc: d.desc || '',
                files: [],
                previews: d.files
                  ? d.files.map(f => ({ url: f.url, name: f.fileName || f.name || 'Unknown' }))
                  : [],
              }))
            : [{ name: '', desc: '', files: [], previews: [] }],
        };
        setInitialData(reportData);
        setInfo({
          reportId: reportData.reportId,
          jobId: reportData.jobId,
          status: reportData.status,
          date: reportData.date,
          customer: reportData.customer,
          equipment: reportData.equipment,
          serial: reportData.serial,
        });
        setDocs(reportData.docs);
        setErrors({ jobId: !reportData.jobId, customer: !reportData.customer, docs: reportData.docs.map(d => !d.name.trim()) });

        // Fetch jobs
        const jSnap = await getDocs(query(collection(db, 'Jobs'), orderBy('createdAt', 'desc')));
        setJobs(jSnap.docs.map(d => d.data().jobId));

        // Fetch customers
        const cSnap = await getDocs(collection(db, 'Customers'));
        setCustomers(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        toast.error('Error fetching data.', { className: 'toast-error' });
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    const unsub = auth.onAuthStateChanged(loadData);
    return () => unsub();
  }, [id, navigate]);

  /* ───────── validation ───────── */
  const validateForm = useCallback(() => {
    const newErrors = {
      jobId: !info.jobId,
      customer: !info.customer,
      docs: docs.map(d => !d.name.trim()),
    };
    setErrors(newErrors);
    return !newErrors.jobId && !newErrors.customer && !newErrors.docs.some(e => e);
  }, [info.jobId, info.customer, docs]);

  /* ───────── form handlers ───────── */
  const changeInfo = useCallback(
    debounce((id, value) => {
      setInfo(p => ({ ...p, [id]: value }));
      if (id === 'jobId' || id === 'customer') {
        setErrors(p => ({ ...p, [id]: !value }));
      }
    }, 300),
    []
  );

  const updateRow = useCallback((idx, fn) => setDocs(p => p.map((d, i) => (i === idx ? fn(d) : d))), []);

  const changeDocField = useCallback(
    (idx, f, v) => {
      updateRow(idx, d => ({ ...d, [f]: v }));
      if (f === 'name') {
        setErrors(p => ({
          ...p,
          docs: p.docs.map((e, i) => (i === idx ? !v.trim() : e)),
        }));
      }
    },
    [updateRow]
  );

  const addFiles = useCallback(
    (idx, fs) => {
      if (!fs.length) return;
      const validFiles = fs.filter(f => {
        if (f.size > 5 * 1024 * 1024) {
          toast.error(`${f.name} exceeds 5MB limit.`, { className: 'toast-error' });
          return false;
        }
        return true;
      });
      if (docs[idx].files.length + validFiles.length + docs[idx].previews.length > 10) {
        toast.error('Maximum 10 files per document.', { className: 'toast-error' });
        return;
      }
      updateRow(idx, d => ({
        ...d,
        files: [...d.files, ...validFiles],
        previews: [...d.previews, ...validFiles.map(f => ({ url: URL.createObjectURL(f), name: f.name }))],
      }));
    },
    [docs, updateRow]
  );

  const removeFile = useCallback(
    (rowIdx, fileIdx) => updateRow(rowIdx, d => ({
      ...d,
      files: d.files.filter((_, i) => i !== fileIdx),
      previews: d.previews.filter((_, i) => i !== fileIdx),
    })),
    [updateRow]
  );

  const addLine = useCallback(() => setDocs(p => [...p, { name: '', desc: '', files: [], previews: [] }]), []);

  const delLine = useCallback(idx => setDocs(p => p.filter((_, i) => i !== idx)), []);

  /* ───────── clear form ───────── */
  const clearForm = useCallback(() => {
    if (initialData) {
      setInfo({
        reportId: initialData.reportId,
        jobId: initialData.jobId,
        status: initialData.status,
        date: initialData.date,
        customer: initialData.customer,
        equipment: initialData.equipment,
        serial: initialData.serial,
      });
      setDocs(initialData.docs);
      setErrors({ jobId: !initialData.jobId, customer: !initialData.customer, docs: initialData.docs.map(d => !d.name.trim()) });
    }
  }, [initialData]);

  /* ───────── cancel handler ───────── */
  const handleCancel = useCallback(() => navigate('/Jobs'), [navigate]);

  /* ───────── upload helper ───────── */
  const uploadAll = useCallback(async () => {
    try {
      return await Promise.all(
        docs.map(async (d, rowIdx) => {
          const safeDoc = d.name.trim() || `Doc${rowIdx + 1}`;
          const uploaded = await Promise.all(
            d.files.map(async f => {
              const refPath = `FailureReports/${info.reportId}/${safeDoc}/${f.name}`;
              const storageRef = ref(storage, refPath);
              await uploadBytes(storageRef, f);
              return { fileName: f.name, url: await getDownloadURL(storageRef) };
            })
          );
          return {
            name: d.name.trim() || '—',
            desc: d.desc.trim() || '—',
            files: [...d.previews.filter(p => p.url.startsWith('https://')).map(p => ({ fileName: p.name, url: p.url })), ...uploaded],
          };
        })
      );
    } catch (err) {
      toast.error('Failed to upload files.', { className: 'toast-error' });
      throw err;
    }
  }, [docs, info.reportId]);

  /* ───────── save report ───────── */
  const saveReport = useCallback(async e => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fill all required fields.', { className: 'toast-error' });
      return;
    }

    try {
      setSaving(true);
      const user = auth.currentUser;
      if (!user) throw new Error('Not logged in');

      const uSnap = await getDoc(doc(db, 'users', user.uid));
      const { firstName = '', lastName = '' } = uSnap.exists() ? uSnap.data() : {};

      const docPayloads = await uploadAll();

      await updateDoc(doc(db, 'FailureReports', id), {
        reportId: info.reportId,
        jobId: info.jobId,
        status: info.status,
        date: info.date,
        customer: info.customer,
        equipment: info.equipment.trim() || '—',
        serial: info.serial.trim() || '—',
        docs: docPayloads,
        updatedAt: new Date().toISOString(),
        authId: user.uid,
        createdBy: `${firstName} ${lastName}`.trim() || '—',
      });

      toast.success('Failure report updated successfully!', { className: 'toast-success' });
      navigate('/Jobs');
    } catch (err) {
      console.error('Error saving report:', err);
      toast.error('Could not update report.', { className: 'toast-error' });
    } finally {
      setSaving(false);
    }
  }, [id, info, docs, navigate, uploadAll, validateForm]);

  /* ───────── cleanup object URLs ───────── */
  useEffect(() => {
    return () => {
      docs.forEach(d => d.previews.forEach(p => {
        if (p.url.startsWith('blob:')) URL.revokeObjectURL(p.url);
      }));
    };
  }, [docs]);

  /* ───────── render ───────── */
  if (loading) {
    return (
      <div className="editjob-page">
        <Sidebar />
        <div className="editjob-loading">
          <FaSpinner className="spin" size={40} />
          <p>Loading failure report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="editjob-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <Sidebar />
      <main className="editjob-main">
        <h2>Edit Failure Report</h2>
        <form className="editjob-form" onSubmit={saveReport}>
          {/* Failure Info */}
          <section>
            <h3>Failure Info</h3>
            <div className="field-row three-cols">
              <div className="field-group">
                <label htmlFor="reportId">Report ID</label>
                <input
                  id="reportId"
                  type="text"
                  value={info.reportId}
                  readOnly
                  aria-label="Report ID (read-only)"
                />
              </div>
              <div className="field-group">
                <label htmlFor="jobId">Job Number <span className="required">*</span></label>
                <select
                  id="jobId"
                  value={info.jobId}
                  onChange={e => changeInfo('jobId', e.target.value)}
                  className={errors.jobId ? 'input-error' : ''}
                  aria-invalid={errors.jobId}
                  aria-describedby={errors.jobId ? 'jobId-error' : undefined}
                >
                  <option value="">Select Job</option>
                  {jobs.map(j => (
                    <option key={j} value={j}>{j}</option>
                  ))}
                </select>
                {errors.jobId && (
                  <span id="jobId-error" className="error-text">Job number is required</span>
                )}
              </div>
              <div className="field-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  value={info.status}
                  onChange={e => changeInfo('status', e.target.value)}
                  aria-label="Report Status"
                >
                  <option>Pending</option>
                  <option>In Progress</option>
                  <option>Completed</option>
                  <option>On Hold</option>
                  <option>Cancelled</option>
                </select>
              </div>
              <div className="field-group">
                <label htmlFor="date">Date</label>
                <input
                  id="date"
                  type="date"
                  value={info.date}
                  onChange={e => changeInfo('date', e.target.value)}
                  aria-label="Report Date"
                />
              </div>
              <div className="field-group">
                <label htmlFor="customer">Customer <span className="required">*</span></label>
                <select
                  id="customer"
                  value={info.customer}
                  onChange={e => changeInfo('customer', e.target.value)}
                  className={errors.customer ? 'input-error' : ''}
                  aria-invalid={errors.customer}
                  aria-describedby={errors.customer ? 'customer-error' : undefined}
                >
                  <option value="">Select Customer</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.name}>{c.name}</option>
                  ))}
                </select>
                {errors.customer && (
                  <span id="customer-error" className="error-text">Customer is required</span>
                )}
              </div>
              <div className="field-group">
                <label htmlFor="equipment">Equipment</label>
                <input
                  id="equipment"
                  value={info.equipment}
                  onChange={e => changeInfo('equipment', e.target.value)}
                  placeholder="Equipment"
                  aria-label="Equipment"
                />
              </div>
              <div className="field-group">
                <label htmlFor="serial">Serial Number</label>
                <input
                  id="serial"
                  value={info.serial}
                  onChange={e => changeInfo('serial', e.target.value)}
                  placeholder="Serial Number"
                  aria-label="Serial Number"
                />
              </div>
            </div>
          </section>

          {/* Failure Docs */}
          <section>
            <h3>Failure Docs</h3>
            <div className="docs-header">
              <div>Name <span className="required">*</span></div>
              <div>Description</div>
              <div>Images</div>
              <div />
            </div>
            {docs.map((d, i) => (
              <div key={i} className="doc-row">
                <div className="field-group">
                  <input
                    placeholder="Document name"
                    value={d.name}
                    onChange={e => changeDocField(i, 'name', e.target.value)}
                    className={errors.docs[i] ? 'input-error' : ''}
                    aria-invalid={errors.docs[i]}
                    aria-describedby={errors.docs[i] ? `doc-name-error-${i}` : undefined}
                  />
                  {errors.docs[i] && (
                    <span id={`doc-name-error-${i}`} className="error-text">Document name is required</span>
                  )}
                </div>
                <input
                  placeholder="Description"
                  value={d.desc}
                  onChange={e => changeDocField(i, 'desc', e.target.value)}
                  aria-label="Document description"
                />
                <div className="upload-cell">
                  <UploadArea
                    previews={d.previews}
                    onAdd={fs => addFiles(i, fs)}
                    onRemove={fi => removeFile(i, fi)}
                    rowIdx={i}
                  />
                </div>
                <button
                  type="button"
                  className="btn-delete"
                  onClick={() => delLine(i)}
                  aria-label="Remove document row"
                >
                  <FaTrashAlt />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn-add"
              onClick={addLine}
              aria-label="Add new document row"
            >
              + Add More
            </button>
          </section>

          <div className="submit-wrap">
            <button
              type="submit"
              className="btn-submit"
              disabled={saving}
              aria-label="Save failure report"
            >
              {saving ? 'Saving...' : 'Save Failure Report'}
            </button>
            <button
              type="button"
              className="btn-clear"
              onClick={clearForm}
              disabled={saving}
              aria-label="Clear form"
            >
              Clear Form
            </button>
            <button
              type="button"
              className="btn-cancel"
              onClick={handleCancel}
              disabled={saving}
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
        </form>
      </main>

      {saving && (
        <div className="saving-overlay">
          <FaSpinner className="spin" size={48} />
          <p>Saving...</p>
        </div>
      )}
    </div>
  );
}

/* ───────── per-row upload widget ───────── */
function UploadArea({ previews, onAdd, onRemove, rowIdx }) {
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);

  const handleInput = useCallback(e => onAdd(Array.from(e.target.files || [])), [onAdd]);
  const over = useCallback(e => {
    e.preventDefault();
    setDrag(true);
  }, []);
  const leave = useCallback(() => setDrag(false), []);
  const drop = useCallback(
    e => {
      e.preventDefault();
      setDrag(false);
      onAdd(Array.from(e.dataTransfer.files || []));
    },
    [onAdd]
  );
  const openDialog = useCallback(() => fileRef.current?.click(), []);

  return (
    <div className="upload-wrapper">
      <div
        className={`upload-area-sm ${drag ? 'drag-active' : ''}`}
        onClick={openDialog}
        onDragOver={over}
        onDragLeave={leave}
        onDrop={drop}
        aria-label={`Upload files for document ${rowIdx + 1}`}
      >
        <FaCloudUploadAlt size={18} />
        <p>{drag ? 'Drop' : 'Click/Drag'}</p>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleInput}
        />
      </div>
      {previews.length > 0 && (
        <div className="thumbnail-row">
          {previews.map((p, idx) => (
            <div key={idx} className="thumb-wrapper">
              <img src={p.url} alt={p.name} className="thumb" />
              <span className="thumb-name">{p.name}</span>
              <button
                type="button"
                className="remove-thumb"
                onClick={() => onRemove(idx)}
                aria-label={`Remove ${p.name}`}
              >
                <FaTimes />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}